import React, { useCallback, useMemo, useState } from "react"
import { EMPTY_COUPON, EMPTY_NOTIFICATION_TEMPLATE, useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import { addValidatorsCoupon, addValidatorsNotificationTemplate } from "../../globalStates/ExhibitorValidation"
import { useLanguageState } from "../../globalStates/LanguageState"
import { addElement, getCountText, removeElementById } from "../../globalStates/utils"
import { newId } from "../../utils/Id"
import Page from "../../components/Page"
import { useConfigDataState } from "../../globalStates/ConfigDataState"
import NotificationTemplatesSection from "./NotificationTemplatesSection"
import { Coupons, NotificationTemplate } from "../../globalStates/ExhibitorDataTypes"
import { Col, Row } from "react-bootstrap"
import NotificationTemplatePackageModal from "./NotificationTemplatePackageModal"
import { useSubelementPackagesState } from "../../globalStates/SubelementPackagesState"
import { ImmutableArray, ImmutableObject, State } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import ButtonWithCaption from "../../components/ButtonWithCaption"

export function getPromotion(
    notificationTemplateState: State<ImmutableObject<NotificationTemplate>, Validation>,
    promotionsStates: State<ImmutableArray<Coupons>, Validation>
): State<ImmutableObject<Coupons>, Validation> | undefined {
    return promotionsStates.find((state) => state.id.get() === notificationTemplateState.action.get().param)
}

export type NotificationTemplatePackage =
    | "notificationTemplate#small"
    | "notificationTemplate#medium"
    | "notificationTemplate#large"

const NotificationTemplatesPage: React.FC<{}> = () => {
    const branding = useLanguageState().getStrings()
    const serviceKeys = useConfigDataState().getServiceKeys()
    const notificationTemplatesStates = useExhibitorDataState().getNotificationTemplates()
    const promotionsStates = useExhibitorDataState().getNotificationPromotions()

    const subelementPackages = useSubelementPackagesState().getSubelementPackages()

    const [packageModalVisible, setPackageModalVisible] = useState<boolean>(false)

    //I know the following part of the code is horrible, but I don't really care, it was urgent
    const canAddNotificationTemplateSmall = useMemo(() => {
        const notificationTemplatesLimit = serviceKeys["notificationTemplate#small"] || 0

        const notificationTemplateStatesForPackage = notificationTemplatesStates.filter(
            (notificationTemplateState) => notificationTemplateState.subelementPackage.get() === "notificationTemplate#small"
        )

        if (notificationTemplateStatesForPackage.length >= notificationTemplatesLimit) return false

        let valid = true
        notificationTemplatesStates?.forEach((notificationTemplate) => {
            const text = notificationTemplate.text.get()

            valid = valid && !!text && text.length >= 0
        })
        return valid
    }, [notificationTemplatesStates, serviceKeys])

    const canAddNotificationTemplateMedium = useMemo(() => {
        const notificationTemplatesLimit = serviceKeys["notificationTemplate#medium"] || 0

        const notificationTemplateStatesForPackage = notificationTemplatesStates.filter(
            (notificationTemplateState) => notificationTemplateState.subelementPackage.get() === "notificationTemplate#medium"
        )

        if (notificationTemplateStatesForPackage.length >= notificationTemplatesLimit) return false

        let valid = true
        notificationTemplatesStates?.forEach((notificationTemplate) => {
            const text = notificationTemplate.text.get()

            valid = valid && !!text && text.length >= 0
        })
        return valid
    }, [notificationTemplatesStates, serviceKeys])

    const canAddNotificationTemplateLarge = useMemo(() => {
        const notificationTemplatesLimit = serviceKeys["notificationTemplate#large"] || 0

        const notificationTemplateStatesForPackage = notificationTemplatesStates.filter(
            (notificationTemplateState) => notificationTemplateState.subelementPackage.get() === "notificationTemplate#large"
        )

        if (notificationTemplateStatesForPackage.length >= notificationTemplatesLimit) return false

        let valid = true
        notificationTemplatesStates?.forEach((notificationTemplate) => {
            const text = notificationTemplate.text.get()

            valid = valid && !!text && text.length >= 0
        })
        return valid
    }, [notificationTemplatesStates, serviceKeys])

    const getNotificationTemplateTotalLimit = (): number => {
        return (
            (serviceKeys["notificationTemplate#small"] || 0) +
            (serviceKeys["notificationTemplate#medium"] || 0) +
            (serviceKeys["notificationTemplate#large"] || 0)
        )
    }

    const onAddNotificationTemplate = useCallback(
        (shopPackage: NotificationTemplatePackage) => {
            const subelementPackage = subelementPackages.find((sp) => sp.id === shopPackage)

            const id = newId().toString()

            //add notification
            addElement<NotificationTemplate>(notificationTemplatesStates, {
                ...EMPTY_NOTIFICATION_TEMPLATE,
                subelementPackage: shopPackage,
                maxUsageCount: subelementPackage?.subelementContingents["notificationTemplate.sendingQuota"] || 500,
                action: {
                    type: "coupon",
                    param: `internal#${id}`
                }
            })

            //add accompanying coupon
            addElement<Coupons>(promotionsStates, {
                ...EMPTY_COUPON,
                id: `internal#${id}`
            })

            addValidatorsNotificationTemplate(notificationTemplatesStates[notificationTemplatesStates.length - 1])
            addValidatorsCoupon(promotionsStates[promotionsStates.length - 1])
        },
        [notificationTemplatesStates, subelementPackages, promotionsStates]
    )

    const onRemoveNotificationTemplate = useCallback(
        (notificationId: string, couponId?: string) => {
            removeElementById<NotificationTemplate>(notificationTemplatesStates, notificationId)

            if (couponId) {
                removeElementById<Coupons>(promotionsStates, couponId)
            }
        },
        [notificationTemplatesStates, promotionsStates]
    )

    return (
        <Page description={branding.configuration.notificationTemplatesPage.pageDescription}>
            <Row style={{ borderBottom: "1px solid #d9d9d9", paddingBottom: "25px", marginBottom: "25px" }}>
                <Col>
                    {notificationTemplatesStates?.map((notificationTemplatesState, index) => (
                        <NotificationTemplatesSection
                            key={notificationTemplatesState.id.get()}
                            notificationTemplateState={notificationTemplatesState}
                            couponState={getPromotion(notificationTemplatesState, promotionsStates)!}
                            index={index}
                            onClickRemove={onRemoveNotificationTemplate}
                        />
                    ))}

                    <ButtonWithCaption
                        id="addNotificationTemplate"
                        caption={
                            branding.showCounter
                                ? getCountText(notificationTemplatesStates.length, getNotificationTemplateTotalLimit())
                                : undefined
                        }
                        buttonText={branding.configuration.notificationTemplatesPage.btnAddNotificationTemplates}
                        onClick={() => setPackageModalVisible(true)}
                        disabled={
                            !(
                                canAddNotificationTemplateSmall ||
                                canAddNotificationTemplateMedium ||
                                canAddNotificationTemplateLarge
                            )
                        }
                    />
                </Col>
            </Row>
            <NotificationTemplatePackageModal
                visible={packageModalVisible}
                onClose={() => setPackageModalVisible(false)}
                onOk={(packageToAdd) => {
                    onAddNotificationTemplate(packageToAdd)
                    setPackageModalVisible(false)
                }}
                notificationTemplatesStates={notificationTemplatesStates}
                subelementPackages={subelementPackages}
                serviceKeys={serviceKeys}
            />
        </Page>
    )
}

export default NotificationTemplatesPage
